import React, { useState, useEffect } from "react";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import { FaCaretDown, FaShoppingCart } from "react-icons/fa";
import { OrderTrack } from "./OrderTrack";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const OrderPage = () => {
  const navigate = useNavigate();
  const [cartCount, setCartCount] = useState(0);
  const [tabs, setTabs] = useState(1);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tabslist = [
    { id: 1, name: "My Orders" },
    { id: 2, name: "My Address" },
    { id: 3, name: "Wallet" },
  ];

  // Fetch orders from sessionStorage (saved from checkout)
  useEffect(() => {
    try {
      setLoading(true);
      setError(null);

      // Get checkout response from sessionStorage
      const checkoutData = sessionStorage.getItem("checkoutResponse");
      
      if (checkoutData) {
        const parsed = JSON.parse(checkoutData);
        // orders array is inside the checkout response
        if (parsed.orders && Array.isArray(parsed.orders)) {
          setOrders(parsed.orders);
        } else {
          setError("No orders found in checkout data");
        }
      } else {
        setError("No order data available. Please complete a checkout first.");
      }
    } catch (err) {
      console.error("Error loading orders:", err);
      setError("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />

      <main className="flex-1 p-6 pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <div>
          <div className="mb-6">
            <div className="flex justify-between">
              <button
                onClick={() => navigate(-1)}
                className="text-sm text-[#095228] font-semibold"
              >
                ← BACK
              </button>
              <div className="flex items-center gap-4">
                <button className="px-4 py-2 bg-green-700 text-white rounded-full hover:bg-green-800 transition">
                  My Profile
                </button>

                <button className="relative px-4 py-2 bg-green-700 text-white rounded-full hover:bg-green-800 transition flex items-center gap-2">
                  <FaShoppingCart />
                  View Cart
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 w-5 h-5 text-xs flex items-center justify-center text-white bg-yellow-400 rounded-full">
                      {cartCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left: Product List */}
              <div className="lg:col-span-2 space-y-4 px-3">
                <div className="mt-10">
                  <div className="flex gap-10">
                    {tabslist.map((res, i) => (
                      <button
                        key={i}
                        onClick={() => setTabs(res.id)}
                        className={`w-[180px] border-b-[3px] text-start text-xl font-semibold py-2 transition duration-200 ${
                          res.id === tabs
                            ? "border-[#095228] text-[#095228]"
                            : "border-[#ccc] text-gray-500"
                        }`}
                      >
                        {res?.name || "xxxxxxx"}
                      </button>
                    ))}
                  </div>

                  {tabs === 1 ? (
                    <div>
                      {loading ? (
                        <div className="text-center p-6 text-gray-500">
                          Loading orders...
                        </div>
                      ) : error ? (
                        <div className="bg-red-50 text-red-600 p-6 rounded-lg mt-10">
                          {error}
                        </div>
                      ) : orders.length === 0 ? (
                        <div className="text-center p-6 text-gray-500 mt-10">
                          No orders found.
                        </div>
                      ) : (
                        orders.map((order) => (
                          <OrderSummary
                            key={order.order_id || order.id}
                            order={order}
                          />
                        ))
                      )}
                    </div>
                  ) : tabs === 2 ? (
                    <div className="mt-10">
                      <p>My Address Section</p>
                    </div>
                  ) : (
                    tabs === 3 && (
                      <div className="mt-10">
                        <p>Wallet Section</p>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Right: Order Summary */}
              <div className="">
                {/* <OrderTrack /> */}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OrderPage;

const OrderSummary = ({ order }) => {
  const [images, setImages] = useState({}); // map productId -> imageUrl

  // Fetch product images
  useEffect(() => {
    if (!order?.order_items?.length) return;

    const idsToFetch = new Set();

    (order.order_items || []).forEach((it) => {
      if (!it.image && !images[it.product_id]) {
        idsToFetch.add(it.product_id);
      }
    });

    if (idsToFetch.size === 0) return;

    const token =
      sessionStorage.getItem("accessToken") ||
      localStorage.getItem("accessToken") ||
      "";

    const fetchForId = async (productId) => {
      try {
        const url = `${API_BASE_URL}/product?id=${encodeURIComponent(productId)}`;
        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { token } : {}),
          },
        });
        if (!res.ok) return null;
        const json = await res.json();
        const result = json?.result;
        const img = result?.product_media?.[0]?.url || result?.image || null;
        return img;
      } catch (e) {
        return null;
      }
    };

    let mounted = true;
    (async () => {
      const updates = {};
      for (const id of idsToFetch) {
        if (images[id]) continue;
        const img = await fetchForId(id);
        if (img && mounted) updates[id] = img;
      }
      if (mounted && Object.keys(updates).length) {
        setImages((prev) => ({ ...prev, ...updates }));
      }
    })();

    return () => {
      mounted = false;
    };
  }, [order, images]);

  // Get order date
  const orderDate = order?.created_at
    ? new Date(order.created_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "N/A";

  // Get estimated delivery date (e.g., 30 days from order date)
  const deliveryDate = order?.created_at
    ? new Date(
        new Date(order.created_at).getTime() + 30 * 24 * 60 * 60 * 1000
      ).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "N/A";

  // Get pricing info
  const pricing = order?.pricing || {};
  const subtotal = pricing.subtotal || 0;
  const deliveryCharges = pricing.delivery_charges || 0;
  const finalAmount = pricing.final_amount || subtotal + deliveryCharges;

  return (
    <div className="bg-white rounded-lg shadow p-6 mt-10">
      <div className="flex items-center justify-between mb-4">
        <span className="text-yellow-700 font-semibold text-lg">
          Order ID: {order?.order_number || `#${order?.order_id || "N/A"}`}
        </span>
        <div className="flex gap-2">
          <button className="px-4 py-2 border border-green-700 rounded hover:bg-green-50 text-green-700 font-medium">
            Invoice
          </button>
          <OrderTrack />
        </div>
      </div>

      <div className="mb-6 flex flex-col md:flex-row items-start md:items-center gap-4">
        <div>
          <span className="text-gray-600">Order date:</span>
          <span className="font-semibold text-black ml-2">{orderDate}</span>
        </div>
        <span className="flex items-center gap-2 text-green-600">
          <svg
            className="w-5 h-5 inline mr-1"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 13l4 4 8-8"
            />
          </svg>
          Estimated delivery: {deliveryDate}
        </span>
        <span
          className={`ml-auto px-3 py-1 rounded-full text-sm font-medium ${
            order?.order_status === "completed"
              ? "bg-green-100 text-green-700"
              : order?.order_status === "pending"
              ? "bg-yellow-100 text-yellow-700"
              : order?.order_status === "new"
              ? "bg-blue-100 text-blue-700"
              : "bg-gray-100 text-gray-700"
          }`}
        >
          {order?.order_status || "pending"}
        </span>
      </div>

      <div className="mb-4 p-3 bg-gray-50 rounded text-sm">
        <div className="flex justify-between">
          <span>Subtotal:</span>
          <span>${Number(subtotal).toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span>Delivery Charges:</span>
          <span>${Number(deliveryCharges).toFixed(2)}</span>
        </div>
        <div className="flex justify-between font-semibold border-t pt-2 mt-2">
          <span>Total:</span>
          <span>${Number(finalAmount).toFixed(2)}</span>
        </div>
      </div>

      <div className="divide-y">
        {(order?.order_items || []).map((item) => {
          const src = item.image || images[item.product_id] || "/image/shoeimg.png";
          return (
            <div key={item.id} className="flex items-center py-4">
              <img
                src={src}
                alt={item.product_name || "Product"}
                className="w-16 h-16 rounded bg-gray-100 mr-5 object-contain"
              />
              <div>
                <h3 className="font-bold">
                  {item.product_name || "Product"}
                </h3>
                <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                <p className="text-sm text-gray-500">
                  Price: ${Number(item.unit_price || item.total_price || 0).toFixed(2)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};